import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableFooter, TableHead, TableRow, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { SalesReportService } from '../../services/salesReport.service';
import { ArAgingReport as ArAgingReportData } from '../../interfaces/sales/salesReport.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { downloadCsv } from '../../utils/csv';

const ArAgingReport: React.FC = () => {
  const toast = useToast();
  const [report, setReport] = useState<ArAgingReportData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const data = await SalesReportService.aging(institutionId);
      setReport(data);
    } catch (e) {
      toast.showApiError(e, 'Failed to load AR aging report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!report) return;
    const headers = ['Customer', '0-30', '31-60', '61-90', '90+', 'Total'];
    const dataRows = report.rows.map(r => [
      r.customerName,
      r.bucket0_30,
      r.bucket31_60,
      r.bucket61_90,
      r.bucket90Plus,
      r.total,
    ]);
    const totalsRow = [
      'TOTALS',
      report.grandBucket0_30,
      report.grandBucket31_60,
      report.grandBucket61_90,
      report.grandBucket90Plus,
      report.grandTotal,
    ];
    downloadCsv('ar-aging.csv', headers, [...dataRows, totalsRow]);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">AR Aging Report</Typography>
          {report && (
            <Typography variant="body2" color="text.secondary">
              As of {report.asOfDate}
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCsv}
          disabled={!report || loading}
        >
          Export CSV
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : report && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell align="right">0–30</TableCell>
                <TableCell align="right">31–60</TableCell>
                <TableCell align="right">61–90</TableCell>
                <TableCell align="right">90+</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.rows.map(row => (
                <TableRow key={row.customerId} hover>
                  <TableCell>
                    <RouterLink
                      to={`/sales/reports/statement?customerId=${row.customerId}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {row.customerName}
                    </RouterLink>
                  </TableCell>
                  <TableCell align="right">{row.bucket0_30.toFixed(2)}</TableCell>
                  <TableCell align="right">{row.bucket31_60.toFixed(2)}</TableCell>
                  <TableCell align="right">{row.bucket61_90.toFixed(2)}</TableCell>
                  <TableCell align="right">{row.bucket90Plus.toFixed(2)}</TableCell>
                  <TableCell align="right">{row.total.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell>TOTALS</TableCell>
                <TableCell align="right">{report.grandBucket0_30.toFixed(2)}</TableCell>
                <TableCell align="right">{report.grandBucket31_60.toFixed(2)}</TableCell>
                <TableCell align="right">{report.grandBucket61_90.toFixed(2)}</TableCell>
                <TableCell align="right">{report.grandBucket90Plus.toFixed(2)}</TableCell>
                <TableCell align="right">{report.grandTotal.toFixed(2)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ArAgingReport;
